import React, { useEffect, useMemo, useState } from "react";
import { Sparkles, Loader2, Check, ShieldAlert, Pill, ClipboardList, AlertCircle, Utensils, Shuffle, Save, FolderOpen, Trash2, MessageCircle, Mail, Printer, MessageSquare, Undo2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { openWhatsApp } from "../lib/whatsapp";

/**
 * AdvancedDietChart — specialist-grade condition-aware diet builder.
 *
 * Flow:
 *   1. Doctor picks a condition profile from the 26-code taxonomy (T2DM,
 *      CKD stage, HF, HTN, MASLD, IBD, Hepatic failure, GLP-1 user, …).
 *   2. Anthropometry auto-loads from the visit (height/weight/waist/sex).
 *   3. On Generate → POST /api/diet/plans/advanced returns a full schema-v2
 *      plan (macros, 7-day menu, foods-to-avoid categorised, electrolyte
 *      guidance, drug-diet interactions, monitoring plan, emergency signs).
 *   4. The doctor reviews inline — every section is a card.
 */
export default function AdvancedDietChart({ patient, defaultAnthro, lang = "en", onPlanSaved, preselectedConditions = [] }) {
  const [conditions, setConditions] = useState([]);       // available taxonomy
  const [selected, setSelected] = useState(preselectedConditions);           // [{code, detail}]
  const [dietStyle, setDietStyle] = useState("vegetarian");
  const [cuisine, setCuisine] = useState("maharashtrian");
  const [anthro, setAnthro] = useState(defaultAnthro || {});
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [genElapsed, setGenElapsed] = useState(0);
  useEffect(() => {
    if (!loading) { setGenElapsed(0); return; }
    const t = setInterval(() => setGenElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [loading]);
  const GEN_STEPS = [
    [0, "Analysing condition profile & safety rules…"],
    [8, "Calculating reference weight & macro prescription…"],
    [16, "Composing a distinct 7-day menu in your cuisine…"],
    [35, "Screening foods to avoid & drug–food interactions…"],
    [52, "Adding monitoring plan & emergency signs…"],
    [65, "Almost done — formatting the chart…"],
  ];
  const genStep = GEN_STEPS.reduce((acc, [at, label]) => (genElapsed >= at ? label : acc), GEN_STEPS[0][1]);
  const genPct = Math.min(95, Math.round((genElapsed / 75) * 100));
  const [physicianNotes, setPhysicianNotes] = useState("");

  // Templates: doctor can save the current plan as a reusable template
  // and quickly Apply matching templates for similar patients.
  const [templates, setTemplates] = useState([]);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [applyingTpl, setApplyingTpl] = useState(false);

  // Auto-refresh anthro if the parent re-fetches the visit
  useEffect(() => { if (defaultAnthro) setAnthro((prev) => ({ ...prev, ...defaultAnthro })); }, [defaultAnthro]);

  // Sync preselected conditions once (parent may fill this after visit load)
  const preselectedKey = JSON.stringify(preselectedConditions?.map((c) => c.code).sort());
  useEffect(() => {
    if (preselectedConditions?.length && selected.length === 0) {
      setSelected(preselectedConditions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedKey]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/diet/condition-codes");
        setConditions(r.data?.conditions || []);
      } catch (e) { console.debug("[silent]", e); }
    })();
  }, []);

  const toggleCode = (code) => {
    setSelected((prev) => prev.some((c) => c.code === code)
      ? prev.filter((c) => c.code !== code)
      : [...prev, { code, detail: {} }]);
  };
  const isSelected = (code) => selected.some((c) => c.code === code);
  const updateDetail = (code, key, value) => {
    setSelected((prev) => prev.map((c) => c.code === code ? { ...c, detail: { ...c.detail, [key]: value } } : c));
  };

  // ── Templates ────────────────────────────────────────────────────────
  // Fetch matching templates whenever the doctor changes the condition
  // selection (before generating). Ranked server-side by overlap so the best
  // match is first.
  const selectedCodesKey = selected.map((c) => c.code).sort().join(",");
  useEffect(() => {
    if (!selected.length) { setTemplates([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/diet/templates?condition_codes=${encodeURIComponent(selectedCodesKey)}`);
        if (!cancelled) setTemplates(r.data?.templates || []);
      } catch (e) { console.debug("[silent]", e); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCodesKey]);

  const applyTemplate = async (tpl) => {
    if (!anthro.height_cm || !anthro.weight_kg) { toast.error("Enter height and weight first"); return; }
    setApplyingTpl(true);
    try {
      const r = await api.post(`/diet/templates/${tpl.template_id}/apply`, {
        patient_id: patient?.patient_id,
        anthro,
        language: lang,
      });
      setPlan(r.data?.plan);
      toast.success(`Applied template: ${tpl.name}`);
      setTemplatesOpen(false);
      if (onPlanSaved) onPlanSaved(r.data?.plan);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply template");
    } finally { setApplyingTpl(false); }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await api.delete(`/diet/templates/${tpl.template_id}`);
      setTemplates((prev) => prev.filter((t) => t.template_id !== tpl.template_id));
      toast.success("Template deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete template");
    }
  };

  const generate = async () => {
    if (!anthro.height_cm || !anthro.weight_kg) { toast.error("Enter height and weight first"); return; }
    if (!selected.length) { toast.warning("Pick at least one condition (or plain if healthy)"); return; }
    setLoading(true);
    try {
      // Start as a background job then poll — a held-open request dies at the
      // ~60s gateway timeout in production (generation takes 40-90s).
      const r = await api.post("/diet/plans/advanced/start", {
        patient_id: patient?.patient_id,
        anthro,
        conditions: selected,
        diet_style: dietStyle,
        cuisine,
        language: lang,
        physician_notes: physicianNotes,
      });
      const jobId = r.data?.job_id;
      const startedAt = Date.now();
      while (true) {
        await new Promise((res) => setTimeout(res, 3000));
        if (Date.now() - startedAt > 240000) {
          toast.error("Generation is taking unusually long — please try again");
          break;
        }
        let s;
        try {
          s = await api.get(`/diet/plans/advanced/job/${jobId}`);
        } catch (pollErr) { continue; }
        if (s.data?.status === "done") {
          setPlan(s.data.plan);
          toast.success("Advanced diet chart generated");
          if (onPlanSaved) onPlanSaved(s.data.plan);
          break;
        }
        if (s.data?.status === "error") {
          toast.error(s.data?.detail || "Could not generate plan");
          break;
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate plan");
    } finally { setLoading(false); }
  };

  // ── Rendering helpers ─────────────────────────────────────────────
  const Card = ({ title, icon, children, testid, tone = "slate" }) => (
    <div className={`bg-white border border-${tone}-200 rounded-md p-4 space-y-2`} data-testid={testid}>
      <div className={`flex items-center gap-2 text-sm font-semibold text-${tone}-800`}>
        {icon}{title}
      </div>
      {children}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="advanced-diet-chart">
      {/* ── Step 1: Conditions ─────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
        <div className="flex items-center gap-2">
          <ShieldAlert size={14} className="text-pink-600" />
          <div className="text-sm font-semibold text-slate-800">Condition profile</div>
          <span className="text-[11px] text-slate-500">— pick every diagnosis that changes the diet</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {conditions.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => toggleCode(c.code)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                isSelected(c.code)
                  ? "bg-pink-600 text-white border-pink-600"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
              data-testid={`adv-cond-${c.code}`}
              title={c.label}
            >
              {isSelected(c.code) && <Check size={10} className="inline mr-1" />}
              {c.label}
            </button>
          ))}
        </div>
        {selected.length > 0 && (
          <div className="pt-2 border-t border-slate-100 space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Optional details</div>
            {selected.map((c) => {
              const meta = conditions.find((x) => x.code === c.code);
              if (!meta?.details?.length) return null;
              return (
                <div key={c.code} className="flex items-center flex-wrap gap-2 text-[11px]">
                  <span className="font-medium text-slate-700 w-28 shrink-0">{meta.label}</span>
                  {meta.details.map((k) => (
                    <input
                      key={k}
                      value={c.detail?.[k] ?? ""}
                      onChange={(e) => updateDetail(c.code, k, e.target.value)}
                      placeholder={k}
                      className="px-2 py-1 border border-slate-200 rounded text-xs w-32"
                      data-testid={`adv-detail-${c.code}-${k}`}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Step 2: Anthropometry + diet style ─────────── */}
      <div className="bg-white border border-slate-200 rounded-md p-4 grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          ["height_cm", "Height (cm)", "number"],
          ["weight_kg", "Weight (kg)", "number"],
          ["waist_cm", "Waist (cm)", "number"],
          ["age", "Age", "number"],
        ].map(([k, label, t]) => (
          <div key={k}>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
            <Input type={t} value={anthro[k] ?? ""} onChange={(e) => setAnthro({ ...anthro, [k]: e.target.value })} className="h-9 mt-0.5" data-testid={`adv-anthro-${k}`} />
          </div>
        ))}
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Diet style</Label>
          <select value={dietStyle} onChange={(e) => setDietStyle(e.target.value)} className="h-9 mt-0.5 w-full border border-slate-200 rounded-md px-2 text-sm" data-testid="adv-diet-style">
            <option value="vegetarian">Vegetarian</option>
            <option value="eggetarian">Eggetarian</option>
            <option value="non_vegetarian">Non-vegetarian</option>
            <option value="vegan">Vegan</option>
            <option value="jain">Jain</option>
          </select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Cuisine</Label>
          <select value={cuisine} onChange={(e) => setCuisine(e.target.value)} className="h-9 mt-0.5 w-full border border-slate-200 rounded-md px-2 text-sm" data-testid="adv-cuisine">
            <option value="maharashtrian">Maharashtrian</option>
            <option value="north_indian">North Indian</option>
            <option value="south_indian">South Indian</option>
            <option value="gujarati">Gujarati</option>
            <option value="bengali">Bengali</option>
            <option value="generic">Generic Indian</option>
          </select>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setTemplatesOpen((v) => !v)}
          disabled={!selected.length}
          className="gap-1.5"
          data-testid="adv-templates-toggle"
          title={selected.length ? "Browse saved templates matching these conditions" : "Pick at least one condition to see templates"}
        >
          <FolderOpen size={14} />
          Templates{templates.length ? ` · ${templates.length}` : ""}
        </Button>
        <Button
          onClick={generate}
          disabled={loading || !anthro.height_cm || !anthro.weight_kg || !selected.length}
          className="bg-pink-600 hover:bg-pink-700 text-white gap-2"
          data-testid="adv-diet-generate"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {loading ? "Generating… (takes ~1 min)" : "Generate Advanced Chart"}
        </Button>
      </div>

      {/* ── Generation progress ─────────────────────────── */}
      {loading && (
        <div className="bg-pink-50/60 border border-pink-200 rounded-md p-3 space-y-2" data-testid="adv-gen-progress">
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-pink-900 font-medium flex items-center gap-1.5">
              <Loader2 size={11} className="animate-spin" /> {genStep}
            </span>
            <span className="text-pink-700 tabular">{genElapsed}s · ~1 min total</span>
          </div>
          <div className="h-1.5 bg-pink-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-pink-600 rounded-full transition-[width] duration-1000 ease-linear"
              style={{ width: `${genPct}%` }}
            />
          </div>
        </div>
      )}

      {/* ── Templates browser ─────────────────────────── */}
      {templatesOpen && (
        <div className="bg-indigo-50/50 border border-indigo-200 rounded-md p-3 space-y-2" data-testid="adv-templates-panel">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold text-indigo-900 flex items-center gap-1.5">
              <FolderOpen size={12} /> Matching templates for <em>{selected.map((c) => c.code).join(" + ") || "—"}</em>
            </div>
            <button type="button" onClick={() => setTemplatesOpen(false)} className="text-[10px] text-slate-500 hover:text-slate-700" data-testid="adv-templates-close">Close</button>
          </div>
          {templates.length === 0 ? (
            <div className="text-[11px] text-slate-500 italic">No saved templates yet. Generate a plan and click <strong>Save as Template</strong> to reuse it next time.</div>
          ) : (
            <div className="space-y-1.5">
              {templates.map((tpl) => (
                <div key={tpl.template_id} className="flex items-center gap-2 bg-white border border-indigo-100 rounded px-2.5 py-1.5" data-testid={`adv-template-${tpl.template_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-semibold text-slate-800 truncate">{tpl.name}</div>
                    <div className="text-[10px] text-slate-500 truncate">
                      {(tpl.condition_codes || []).join(" + ") || "General"}
                      <span className="mx-1">·</span>
                      <span className={tpl.scope === "clinic" ? "text-emerald-700" : "text-amber-700"}>
                        {tpl.scope === "clinic" ? "Clinic-wide" : "Private"}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => applyTemplate(tpl)}
                    disabled={applyingTpl}
                    className="text-[10px] px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                    data-testid={`adv-template-apply-${tpl.template_id}`}
                  >
                    {applyingTpl ? "Applying…" : "Apply"}
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteTemplate(tpl)}
                    className="text-slate-400 hover:text-rose-600"
                    title="Delete template"
                    data-testid={`adv-template-delete-${tpl.template_id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Result ─────────────────────────────────────── */}
      {plan && (
        <div className="space-y-3">
          {/* Menu-source badge — tiny chip telling the doctor whether the
              7-day menu came straight from Gemini or from the deterministic
              fallback bank (kicks in when Gemini can't cope with the
              condition stack — see diet_menu_bank.py). "hybrid" is reserved
              for a future path where Gemini fills only some days. */}
          {plan.menu_source && (
            <div className="flex items-center gap-1.5" data-testid="menu-source-badge">
              {plan.menu_source === "fallback" ? (
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 font-semibold">
                  <ClipboardList size={10} /> Curated menu
                  <span className="font-normal text-amber-700/80">(condition-safe rotation)</span>
                </span>
              ) : plan.menu_source === "hybrid" ? (
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-800 border border-indigo-200 font-semibold">
                  <Sparkles size={10} /> Hybrid menu
                  <span className="font-normal text-indigo-700/80">(AI + curated)</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 font-semibold">
                  <Sparkles size={10} /> AI menu
                  <span className="font-normal text-emerald-700/80">(Gemini)</span>
                </span>
              )}
            </div>
          )}
          {/* Save-to-Patient bar — persistent chip with quick share actions */}
          <SaveToPatientBar
            plan={plan}
            patient={patient}
            lang={lang}
            onOpenSaveTpl={() => setSaveTplOpen(true)}
            onTemplateSaved={(tpl) => {
              // Refresh the templates list so the new one appears immediately.
              setTemplates((prev) => [tpl, ...prev]);
            }}
            saveTplOpen={saveTplOpen}
            setSaveTplOpen={setSaveTplOpen}
          />
          {/* Macro Rx card */}
          <Card title="Nutritional Prescription" icon={<ClipboardList size={14} className="text-emerald-600" />} testid="adv-plan-macros">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
              {[
                ["Energy", `${plan.prescription?.energy_kcal?.value} kcal`],
                ["Protein", `${plan.prescription?.protein?.grams} g @ ${plan.prescription?.protein?.g_per_kg} g/kg`],
                ["Carbs", `${plan.prescription?.carbs_g?.value} g`],
                ["Fat", `${plan.prescription?.fat_g?.value} g`],
                ["Sodium", `${plan.prescription?.sodium_mg} mg (≤${plan.prescription?.salt_g} g salt)`],
                ["Fluid", `${plan.prescription?.fluid_ml?.value} ml/day`],
                ["Fibre", `${plan.prescription?.fibre_g?.value} g`],
                ["Oil", `${plan.prescription?.cooking_oil_tsp} tsp/day`],
                ["Ref. weight", `${plan.anthro?.reference_weight_kg} kg (${plan.anthro?.reference_weight_basis})`],
                ["Loss pace", `${plan.prescription?.weight_loss_pace_kg_per_week || 0} kg/wk`],
              ].map(([k, v]) => (
                <div key={k} className="border border-slate-100 rounded px-2 py-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k}</div>
                  <div className="text-slate-800 font-medium">{v}</div>
                </div>
              ))}
            </div>
            {plan.clinical_rationale_md && (
              <div className="text-xs text-slate-700 whitespace-pre-wrap border-t border-slate-100 pt-2 mt-2 leading-relaxed">
                {plan.clinical_rationale_md}
              </div>
            )}
          </Card>

          {/* 7-day menu */}
          {plan.menu_7day?.length > 0 && (
            <Card title="7-day menu (distinct days)" icon={<Utensils size={14} className="text-amber-600" />} testid="adv-plan-menu">
              <div className="space-y-2">
                {plan.menu_7day.map((day, i) => (
                  <details key={i} className="border border-slate-100 rounded" open={i === 0}>
                    <summary className="cursor-pointer px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 hover:bg-slate-100">
                      Day {day.day || i + 1}
                    </summary>
                    <div className="px-3 py-2 divide-y divide-slate-100">
                      {(day.meals || []).map((m, j) => (
                        <div key={j} className="py-1.5">
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{m.meal}</div>
                          {(m.items || []).map((it, k) => (
                            <SwappableItem
                              key={k}
                              plan={plan}
                              day={day.day || i + 1}
                              mealIndex={j}
                              itemIndex={k}
                              item={it}
                              onApplied={(newItem) => {
                                const nextPlan = JSON.parse(JSON.stringify(plan));
                                nextPlan.menu_7day[i].meals[j].items[k] = newItem;
                                setPlan(nextPlan);
                              }}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  </details>
                ))}
              </div>
            </Card>
          )}

          {/* Foods to avoid */}
          {plan.foods_to_avoid?.length > 0 && (
            <Card title="Foods to avoid — categorised" icon={<ShieldAlert size={14} className="text-rose-600" />} testid="adv-plan-avoid">
              <div className="space-y-1.5">
                {plan.foods_to_avoid.map((cat, i) => (
                  <div key={i} className="text-xs">
                    <span className="font-semibold text-rose-700">{cat.reason}:</span>{" "}
                    <span className="text-slate-700">{(cat.items || []).join(", ")}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Medication interactions */}
          {plan.medication_interactions?.length > 0 && (
            <Card title="Drug-diet interactions" icon={<Pill size={14} className="text-indigo-600" />} testid="adv-plan-interactions">
              <div className="space-y-1.5">
                {plan.medication_interactions.map((m, i) => {
                  const drug = typeof m === "string" ? "" : m.drug;
                  const rule = typeof m === "string" ? m : m.rule;
                  return (
                    <div key={i} className="text-xs text-slate-800">
                      {drug && <span className="font-semibold text-indigo-700">{drug}: </span>}
                      {rule}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Monitoring */}
          {plan.monitoring?.length > 0 && (
            <Card title="Monitoring & adjustment" icon={<ClipboardList size={14} className="text-slate-600" />} testid="adv-plan-monitoring">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                {plan.monitoring.map((m, i) => (
                  <div key={i} className="text-xs py-0.5 flex justify-between gap-2 border-b border-slate-50 last:border-0">
                    <span className="text-slate-700">{m.parameter}</span>
                    <span className="text-slate-500">{m.frequency}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Emergency signs */}
          {plan.emergency_signs?.length > 0 && (
            <Card title="Emergency signs — call the clinic" icon={<AlertCircle size={14} className="text-rose-600" />} testid="adv-plan-emergency" tone="rose">
              <ul className="list-disc list-inside space-y-0.5 text-xs text-rose-800">
                {plan.emergency_signs.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </Card>
          )}

          <div className="text-[10px] text-slate-500 text-right flex items-center justify-end gap-3">
            <a
              href={`/diet/print/${plan.plan_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-pink-700 hover:underline inline-flex items-center gap-1 text-[11px] font-medium"
              data-testid="adv-plan-print"
            >
              🖨️ Print / Save PDF
            </a>
            <span>Plan ID: <code>{plan.plan_id}</code> · Guidelines: {plan.generated_by?.guidelines_cited?.join(" · ")}</span>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── SwappableItem ────────────────────────────────────────────────────
// A single menu row with an AI-driven Swap chip. Fetches 3 alternatives on
// demand and lets the doctor apply one — mutates the persisted plan.
// The swap dialog now shows a LIVE nutrition budget for the current day so
// the doctor can see how each candidate swap would move kcal / protein / Na
// against the daily prescription.
function SwappableItem({ plan, day, mealIndex, itemIndex, item, onApplied }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [swaps, setSwaps] = useState([]);

  // Sum current-day totals across every item in every meal of this day.
  const dayIdx = plan.menu_7day.findIndex((d, i) => (d.day || i + 1) === day);
  const dayObj = dayIdx >= 0 ? plan.menu_7day[dayIdx] : null;
  const sumField = (f) => (dayObj?.meals || []).reduce((total, m) =>
    total + (m.items || []).reduce((s, it) => s + (Number(it?.[f]) || 0), 0), 0);
  const totalKcal = sumField("kcal");
  const totalProtein = sumField("protein_g");
  const totalNa = sumField("sodium_mg");

  // Daily budgets from prescription
  const budgetKcal = plan.prescription?.energy_kcal?.value || 0;
  const budgetProtein = plan.prescription?.protein?.grams || 0;
  const budgetNa = plan.prescription?.sodium_mg || 0;

  const load = async () => {
    setOpen(true);
    if (swaps.length) return;
    setLoading(true);
    try {
      const r = await api.post(`/diet/plans/${plan.plan_id}/swap-item`, {
        day, meal_index: mealIndex, item_index: itemIndex,
      });
      setSwaps(r.data?.swaps || []);
      if (!(r.data?.swaps || []).length) toast.warning("No swap suggestions available");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Swap suggestion failed");
    } finally { setLoading(false); }
  };

  const apply = async (newItem) => {
    setApplying(true);
    const previousItem = item;  // capture for undo
    try {
      await api.post(`/diet/plans/${plan.plan_id}/apply-swap`, {
        day, meal_index: mealIndex, item_index: itemIndex, new_item: newItem,
      });
      onApplied?.(newItem);
      setOpen(false);
      setSwaps([]);
      // Show a 5-second Undo toast — one tap restores the previous item.
      toast.success("Menu updated", {
        duration: 5000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/diet/plans/${plan.plan_id}/apply-swap`, {
                day, meal_index: mealIndex, item_index: itemIndex, new_item: previousItem,
              });
              onApplied?.(previousItem);
              toast.success("Swap undone");
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Could not undo swap");
            }
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply swap");
    } finally { setApplying(false); }
  };

  // Predict what the day total would look like if this swap replaces the current item.
  const preview = (candidate) => ({
    kcal:    totalKcal    - (Number(item?.kcal) || 0)       + (Number(candidate?.kcal) || 0),
    protein: totalProtein - (Number(item?.protein_g) || 0)  + (Number(candidate?.protein_g) || 0),
    na:      totalNa      - (Number(item?.sodium_mg) || 0)  + (Number(candidate?.sodium_mg) || 0),
  });

  // Coloured badge: green if within budget, amber if within 15% over, rose if over 15%.
  const tone = (used, budget) => {
    if (!budget) return "text-slate-500";
    const r = used / budget;
    if (r <= 1) return "text-emerald-700";
    if (r <= 1.15) return "text-amber-700";
    return "text-rose-700";
  };

  return (
    <div className="pl-2 py-0.5">
      <div className="flex items-start gap-2 text-xs text-slate-800">
        <span className="mt-0.5">•</span>
        <span className="flex-1">
          {item.name_en || item.name}
          <span className="text-slate-500"> {item.qty_desc || item.qty_desc_en}</span>
          {item.kcal ? <span className="text-slate-500"> · {item.kcal} kcal</span> : null}
          {item.protein_g ? <span className="text-slate-500"> · {item.protein_g} g protein</span> : null}
        </span>
        <button
          type="button"
          onClick={load}
          className="text-[10px] text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 hover:bg-indigo-100"
          data-testid={`swap-btn-d${day}-m${mealIndex}-i${itemIndex}`}
          title="Suggest swap alternatives (AI)"
        >
          <Shuffle size={10} /> Swap
        </button>
      </div>
      {open && (
        <div className="mt-1 ml-4 border border-indigo-100 bg-indigo-50/30 rounded p-2 space-y-1.5">
          {/* Live Nutrition Budget for the day */}
          <div className="flex items-center flex-wrap gap-2 text-[10px] pb-1.5 border-b border-indigo-100" data-testid={`swap-budget-d${day}`}>
            <span className="uppercase tracking-wider text-slate-500 font-semibold">Day {day} total →</span>
            <span className={tone(totalKcal, budgetKcal)}>
              <strong>{totalKcal}</strong> / {budgetKcal || "—"} kcal
            </span>
            <span className={tone(totalProtein, budgetProtein)}>
              <strong>{totalProtein}</strong> / {budgetProtein || "—"} g protein
            </span>
            <span className={tone(totalNa, budgetNa)}>
              <strong>{totalNa}</strong> / {budgetNa || "—"} mg Na
            </span>
          </div>
          {loading && <div className="text-[10px] text-slate-500 flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> Thinking of alternatives…</div>}
          {!loading && swaps.length === 0 && <div className="text-[10px] text-slate-500 italic">No suggestions.</div>}
          {swaps.map((s, i) => {
            const pv = preview(s);
            return (
              <div key={i} className="flex items-start gap-2 text-[11px]">
                <div className="flex-1">
                  <div className="font-medium text-slate-800">{s.name_en}{s.name_mr ? ` (${s.name_mr})` : ""}</div>
                  <div className="text-[10px] text-slate-500">{s.qty_desc} · ≈{s.kcal || "?"} kcal · {s.protein_g || "?"} g protein{s.notes ? ` · ${s.notes}` : ""}</div>
                  <div className="text-[10px] mt-0.5" data-testid={`swap-preview-d${day}-m${mealIndex}-i${itemIndex}-s${i}`}>
                    <span className="text-slate-500 mr-1">If applied:</span>
                    <span className={tone(pv.kcal, budgetKcal)}>{pv.kcal} kcal</span>
                    <span className="text-slate-300 mx-1">·</span>
                    <span className={tone(pv.protein, budgetProtein)}>{pv.protein} g protein</span>
                    <span className="text-slate-300 mx-1">·</span>
                    <span className={tone(pv.na, budgetNa)}>{pv.na} mg Na</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => apply(s)}
                  disabled={applying}
                  className="text-[10px] px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 self-center"
                  data-testid={`swap-apply-d${day}-m${mealIndex}-i${itemIndex}-s${i}`}
                >Use this</button>
              </div>
            );
          })}
          <button type="button" onClick={() => { setOpen(false); setSwaps([]); }} className="text-[10px] text-slate-500 hover:text-slate-700 mt-0.5">Cancel</button>
        </div>
      )}
    </div>
  );
}



// ─── SaveToPatientBar ─────────────────────────────────────────────────
// Persistent chip shown right below the generated plan. Confirms the plan
// is linked to <patient> and offers one-tap Share actions:
//   • WhatsApp (live — opens wa.me with the print link + summary)
//   • Email    (live — mailto: with subject + body pre-filled)
//   • SMS      (placeholder — clinic SMS provider coming soon)
//   • Print / PDF (opens the A4 vernacular printout)
// Also embeds "Save as Template" so the doctor can turn this plan into a
// reusable template keyed by the current condition combo.
function SaveToPatientBar({ plan, patient, lang, onOpenSaveTpl, onTemplateSaved, saveTplOpen, setSaveTplOpen }) {
  const printPath = `/diet/print/${plan.plan_id}`;
  const printUrl = `${window.location.origin}${printPath}`;
  const conditionsLabel = (plan.profile?.conditions || []).map((c) => c.code).join(" + ") || "General";
  const patientName = patient?.name || "your patient";
  const kcal = plan.prescription?.energy_kcal?.value;
  const proteinG = plan.prescription?.protein?.grams;

  const shareText =
    `${patientName} — advanced diet chart (${conditionsLabel})\n` +
    (kcal ? `Target: ${kcal} kcal/day · Protein ${proteinG || "?"} g\n` : "") +
    `Open full plan: ${printUrl}`;

  // Fire-and-forget share telemetry — never blocks the actual share.
  const logShare = (channel) => {
    api.post(`/diet/plans/${plan.plan_id}/share-log`, { channel }).catch((e) => {
      console.debug("[share-log silent]", e?.message || e);
    });
  };

  const waHandler = () => {
    const mob = patient?.mobile || patient?.phone;
    if (!mob) { toast.error("Patient has no mobile on file"); return; }
    logShare("whatsapp");
    openWhatsApp(mob, shareText);
  };
  const emailHandler = () => {
    const email = patient?.email;
    if (!email) {
      toast.warning("No email on file — opening mail client anyway");
    }
    logShare("email");
    const subject = encodeURIComponent(`Your diet chart from the clinic`);
    const body = encodeURIComponent(shareText);
    const href = `mailto:${email || ""}?subject=${subject}&body=${body}`;
    // Anchor click — avoids sandbox issues with window.location.href in iframe.
    const a = document.createElement("a");
    a.href = href;
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  const smsHandler = () => {
    toast.info("SMS sharing is coming soon — send via WhatsApp or Email for now.");
  };
  const printHandler = () => {
    logShare("print");
    // The anchor already opens the print route in a new tab; we only need to
    // log the tap here. Nothing else to do.
  };

  return (
    <div className="bg-pink-50 border border-pink-200 rounded-md p-3 space-y-2" data-testid="save-to-patient-bar">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[12px] text-pink-900 flex items-center gap-1.5">
          <ClipboardList size={13} className="text-pink-700" />
          This plan is linked to{" "}
          <strong className="text-pink-900" data-testid="linked-patient-name">{patient?.name || "—"}</strong>
          {patient?.patient_id && <span className="text-[10px] text-pink-600 ml-1">({patient.patient_id})</span>}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            type="button"
            onClick={waHandler}
            className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid="save-to-patient-whatsapp"
          >
            <MessageCircle size={12} /> WhatsApp
          </button>
          <button
            type="button"
            onClick={emailHandler}
            className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded bg-sky-600 text-white hover:bg-sky-700"
            data-testid="save-to-patient-email"
          >
            <Mail size={12} /> Email
          </button>
          <button
            type="button"
            onClick={smsHandler}
            className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-100 text-slate-500 border border-slate-200 cursor-not-allowed"
            title="SMS provider not configured — coming soon"
            data-testid="save-to-patient-sms"
          >
            <MessageSquare size={12} /> SMS <span className="text-[9px] italic ml-0.5">(soon)</span>
          </button>
          <a
            href={printPath}
            target="_blank"
            rel="noopener noreferrer"
            onClick={printHandler}
            className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-900 text-white hover:bg-slate-800"
            data-testid="save-to-patient-print"
          >
            <Printer size={12} /> Print / PDF
          </a>
          <button
            type="button"
            onClick={onOpenSaveTpl}
            className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-pink-300 text-pink-800 bg-white hover:bg-pink-100"
            data-testid="save-as-template-btn"
          >
            <Save size={12} /> Save as Template
          </button>
        </div>
      </div>
      {saveTplOpen && (
        <SaveTemplateDialog
          plan={plan}
          onClose={() => setSaveTplOpen(false)}
          onSaved={(tpl) => { onTemplateSaved?.(tpl); setSaveTplOpen(false); }}
        />
      )}
    </div>
  );
}


// ─── SaveTemplateDialog ───────────────────────────────────────────────
// Inline dialog: auto-suggests a name from the plan's condition codes
// (doctor can edit) and lets them choose Clinic-wide vs Private scope.
function SaveTemplateDialog({ plan, onClose, onSaved }) {
  const initialName = useMemo(() => {
    const codes = (plan.profile?.conditions || []).map((c) => c.code);
    return codes.length ? codes.join(" + ") : "General Diet";
  }, [plan]);
  const [name, setName] = useState(initialName);
  const [scope, setScope] = useState("clinic");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error("Template name is required"); return; }
    setSaving(true);
    try {
      const r = await api.post("/diet/templates", {
        plan_id: plan.plan_id,
        name: name.trim(),
        scope,
      });
      toast.success(`Saved template: ${r.data?.template?.name}`);
      onSaved?.(r.data?.template);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save template");
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-2 border border-pink-200 rounded bg-white p-3 space-y-2" data-testid="save-template-dialog">
      <div className="text-[11px] font-semibold text-pink-900 flex items-center gap-1.5">
        <Save size={11} /> Save this plan as a reusable template
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-end">
        <div>
          <Label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Template name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 mt-0.5" data-testid="save-template-name" />
        </div>
        <div>
          <Label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Visibility</Label>
          <div className="flex gap-1.5 mt-0.5">
            <button
              type="button"
              onClick={() => setScope("clinic")}
              className={`text-[10px] px-2 py-1 rounded border ${scope === "clinic" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200"}`}
              data-testid="save-template-scope-clinic"
            >Clinic-wide</button>
            <button
              type="button"
              onClick={() => setScope("private")}
              className={`text-[10px] px-2 py-1 rounded border ${scope === "private" ? "bg-amber-600 text-white border-amber-600" : "bg-white text-slate-600 border-slate-200"}`}
              data-testid="save-template-scope-private"
            >Just me</button>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-1.5 pt-1">
        <button type="button" onClick={onClose} className="text-[10px] text-slate-500 hover:text-slate-700 px-2 py-1" data-testid="save-template-cancel">Cancel</button>
        <button
          type="button"
          onClick={submit}
          disabled={saving}
          className="text-[10px] px-2 py-1 rounded bg-pink-600 text-white hover:bg-pink-700 disabled:opacity-50"
          data-testid="save-template-submit"
        >
          {saving ? "Saving…" : "Save Template"}
        </button>
      </div>
    </div>
  );
}
